import { Resource } from "@core/shared/types";
import { parseError } from "@core/shared/utils";
import { useAction } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { getActiveForm } from "@mongez/react-form";
import { useCallback, useRef, useState } from "react";
import { errorToast, toastConfirm, toastLoading } from "../components/toast";

type ResourceOptions = {
  defaultValue?: {
    url: string;
    id: string;
  };
  type?: string;
  reference?: {
    type: string;
    typeId: string;
  };
  category?: string;
  title?: string;
  onDelete?: () => void;
  onChange?: (resource: Resource) => void;
};

export function useResourceImage(props: ResourceOptions) {
  const [displayedImage, setDisplayedImage] = useState(
    props.defaultValue?.url || "",
  );

  const [resource, setResource] = useState(props.defaultValue);

  const resourcesAction = useAction("/resources");

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDeletedRef = useRef(false);

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        errorToast("Please upload an image file");
        return;
      }

      const activeForm = getActiveForm();

      if (activeForm) {
        activeForm.submitting(false);
      }

      // If trying to upload while there is a resource, remove the current resource while uploading
      if (resource) {
        resourcesAction({
          resource: resource.id,
          action: "delete",
        });
        setResource(undefined);
        isDeletedRef.current = true;
      }

      const loader = toastLoading(trans("uploading"));
      try {
        setIsUploading(true);
        const activeForm = getActiveForm();

        if (activeForm) {
          activeForm.submitting(false);
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", props.type || "image");

        if (props.reference) {
          formData.append("referenceType", props.reference.type);
          formData.append("referenceId", props.reference.typeId.toString());
        }

        if (props.category) {
          formData.append("category", props.category);
        }

        if (props.title) {
          formData.append("title", props.title);
        }

        formData.append("action", "upload");

        const response = await resourcesAction(formData);

        setDisplayedImage(response.data.resource.url);
        props.onChange?.(response.data.resource);
        setResource(response.data.resource);
        loader.dismiss();
      } catch (error) {
        loader.error(parseError(error));
        errorToast(parseError(error));
        console.error(error);
      } finally {
        setIsUploading(false);
        if (activeForm) {
          activeForm.submitting(true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.reference, props.category, props.onChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }

      // reset the file input
      e.target.value = "";
    },
    [handleFileUpload],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemove = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const confirmed = await toastConfirm(
        trans("deleteResourceConfirm"),
        trans("deleteResourceMessage"),
      );

      if (!confirmed) return;

      setDisplayedImage("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (resource) {
        resourcesAction({
          resource: resource.id,
          action: "delete",
        });
        isDeletedRef.current = true;
        setResource(undefined);
      }

      props.onDelete?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resource],
  );

  return {
    displayedImage,
    isDragging,
    isUploading,
    fileInputRef,
    isDeletedRef,
    handleFileUpload,
    handleDrop,
    handleRemove,
    handleClick,
    resource,
    handleDragOver,
    handleDragLeave,
    handleFileInput,
  };
}
